import { useEffect } from 'react'
import { NavLink } from 'react-router-dom-v5-compat'

import { useSplitEnabled } from '@buffer-mono/features'
import {
  Sidebar,
  Text,
  UserIcon,
  Flex,
  SavedRepliesIcon,
  FolderIcon,
  UpgradeBadge,
  Heading,
  useSidebar,
  useIsMobileScreenSize,
  // Icons
  AllChannelsIcon,
  AudienceIcon,
  BellIcon,
  BuildingIcon,
  DollarIcon,
  FlaskConicalIcon,
  GripIcon,
  MessageCircleHeartIcon,
  SlidersVerticalIcon,
  NewBadge,
  TagIcon,
  BetaBadge,
} from '@buffer-mono/popcorn'

import {
  useCurrentOrganization,
  useIsFreeUser,
  useIsOneBufferOrg,
} from '~publish/contexts/AccountContext'
import { usePageTitle } from '~publish/hooks/usePageTitle'
import {
  betaFeaturesRoute,
  generalSettingsRoute,
  referAFriendRoute,
  settingsBillingRoute,
  settingsChannelGroupsRoute,
  settingsChannelsRoute,
  settingsSavedRepliesRoute,
  settingsTagsRoute,
} from '~publish/legacy/routes'

import { useCanAccessTeamFeatures } from './hooks/useCanAccessTeamFeatures'
import useConnectedChannelsCount from './hooks/useConnectedChannelsCount'

import style from './SettingsSidebar.module.css'

const SettingsSidebar = ({
  className,
}: {
  className?: string
}): JSX.Element => {
  usePageTitle('Settings')

  const currentOrganization = useCurrentOrganization()
  const isOneBufferOrganization = useIsOneBufferOrg()
  const showBetaFeatures = Boolean(
    !currentOrganization || isOneBufferOrganization,
  )

  const { setOpen, open } = useSidebar()

  const { isEnabled: commentsEnabled } = useSplitEnabled('geid-comments')
  const { isEnabled: tagsEnabled } = useSplitEnabled(
    'tags-management-on-settings',
  )
  const isFreeUser = useIsFreeUser()

  const showTagsButton = tagsEnabled

  const channelCountBadge = useConnectedChannelsCount()

  const { canAccessTeamFeatures } = useCanAccessTeamFeatures()
  const isMobile = useIsMobileScreenSize()

  useEffect(() => {
    if (isMobile) {
      setOpen(false)
    }
  }, [isMobile, setOpen])

  return (
    <Sidebar
      collapsible={isMobile ? 'icon' : 'none'}
      mobileFallback={true}
      className={className}
    >
      <Sidebar.Content className={style.sidebar}>
        {/* Account Section */}
        <Sidebar.Header className={style.sidebarHeader}>
          <Flex justify="between" align="center">
            {open && !isMobile && (
              <Heading as="h3" size="small" className={style.heading}>
                Settings
              </Heading>
            )}
            {isMobile && <Sidebar.Trigger onClick={() => setOpen(!open)} />}
          </Flex>
        </Sidebar.Header>
        <Sidebar.Group>
          <Sidebar.GroupLabel>Account</Sidebar.GroupLabel>
          <Sidebar.GroupContent>
            <Sidebar.List>
              <Sidebar.ListItem>
                <Sidebar.Button
                  prefix={<UserIcon />}
                  as={NavLink}
                  end
                  to="/settings"
                  onClick={() => {
                    if (isMobile) {
                      setOpen(false)
                    }
                  }}
                >
                  Profile
                </Sidebar.Button>
              </Sidebar.ListItem>
              <Sidebar.ListItem>
                <Sidebar.Button
                  prefix={<SlidersVerticalIcon />}
                  as={NavLink}
                  to="/settings/preferences"
                >
                  Preferences
                </Sidebar.Button>
              </Sidebar.ListItem>
              <Sidebar.ListItem>
                <Sidebar.Button
                  prefix={<BellIcon />}
                  as={NavLink}
                  to="/settings/notifications"
                >
                  Notifications
                </Sidebar.Button>
              </Sidebar.ListItem>
            </Sidebar.List>
          </Sidebar.GroupContent>
        </Sidebar.Group>

        {/* Organization Section */}
        <Sidebar.Group>
          <Sidebar.GroupLabel>Organization</Sidebar.GroupLabel>
          <Sidebar.GroupContent>
            <Sidebar.List>
              <Sidebar.ListItem>
                <Sidebar.Button
                  prefix={<BuildingIcon />}
                  as={NavLink}
                  to={generalSettingsRoute.route}
                >
                  General
                </Sidebar.Button>
              </Sidebar.ListItem>
              <Sidebar.ListItem>
                <Sidebar.Button
                  prefix={<AllChannelsIcon />}
                  as={NavLink}
                  to={settingsChannelsRoute.route}
                  suffix={
                    <Text weight="medium" className={style.channelCountBadge}>
                      {channelCountBadge}
                    </Text>
                  }
                >
                  Channels
                </Sidebar.Button>
              </Sidebar.ListItem>
              {canAccessTeamFeatures && (
                <Sidebar.ListItem>
                  <Sidebar.Button
                    prefix={<AudienceIcon />}
                    as={NavLink}
                    to="/settings/team"
                  >
                    Team
                  </Sidebar.Button>
                </Sidebar.ListItem>
              )}
              <Sidebar.ListItem>
                <Sidebar.Button
                  prefix={<DollarIcon />}
                  as={NavLink}
                  to={settingsBillingRoute.route}
                >
                  Billing
                </Sidebar.Button>
              </Sidebar.ListItem>
            </Sidebar.List>
          </Sidebar.GroupContent>
        </Sidebar.Group>

        {/* Features Section */}
        <Sidebar.Group>
          <Sidebar.GroupLabel>Features</Sidebar.GroupLabel>
          <Sidebar.GroupContent>
            <Sidebar.List>
              {showTagsButton && (
                <Sidebar.ListItem>
                  <Sidebar.Button
                    prefix={<TagIcon />}
                    as={NavLink}
                    to={settingsTagsRoute.route}
                    suffix={<BetaBadge size="xsmall" />}
                  >
                    Tags
                  </Sidebar.Button>
                </Sidebar.ListItem>
              )}
              <Sidebar.ListItem>
                <Sidebar.Button
                  prefix={<FolderIcon />}
                  as={NavLink}
                  to={settingsChannelGroupsRoute.route}
                  suffix={
                    isFreeUser ? (
                      <UpgradeBadge size="xsmall" />
                    ) : (
                      <NewBadge size="xsmall" />
                    )
                  }
                >
                  Channel Groups
                </Sidebar.Button>
              </Sidebar.ListItem>
              {commentsEnabled && (
                <Sidebar.ListItem>
                  <Sidebar.Button
                    prefix={<SavedRepliesIcon />}
                    as={NavLink}
                    to={settingsSavedRepliesRoute.route}
                  >
                    Saved Replies
                  </Sidebar.Button>
                </Sidebar.ListItem>
              )}
            </Sidebar.List>
          </Sidebar.GroupContent>
        </Sidebar.Group>

        {/* Other Section */}
        <Sidebar.Group>
          <Sidebar.GroupLabel>Other</Sidebar.GroupLabel>
          <Sidebar.GroupContent>
            <Sidebar.List>
              <Sidebar.ListItem>
                <Sidebar.Button
                  prefix={<GripIcon />}
                  as={NavLink}
                  to="/settings/apps-extras"
                >
                  Apps & Extras
                </Sidebar.Button>
              </Sidebar.ListItem>
              {showBetaFeatures && (
                <Sidebar.ListItem>
                  <Sidebar.Button
                    prefix={<FlaskConicalIcon />}
                    as={NavLink}
                    to={betaFeaturesRoute.route}
                  >
                    Beta Features
                  </Sidebar.Button>
                </Sidebar.ListItem>
              )}
              <Sidebar.ListItem>
                <Sidebar.Button
                  prefix={<MessageCircleHeartIcon />}
                  as={NavLink}
                  to={referAFriendRoute.route}
                >
                  Refer a Friend
                </Sidebar.Button>
              </Sidebar.ListItem>
            </Sidebar.List>
          </Sidebar.GroupContent>
        </Sidebar.Group>
      </Sidebar.Content>
    </Sidebar>
  )
}

export default SettingsSidebar
